// =====================================================
// CHECK-IN BUTTON
// Stage 3: Attendance Tracking
// Button for checking in individual participants
// =====================================================

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, X, RotateCcw, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAttendanceMutations } from '../hooks/useEventParticipants';
import { useToast } from '@/hooks/use-toast';

// ===================
// TYPES
// ===================

interface CheckInButtonProps {
  participantId: string;
  attendanceStatus: 'PENDING' | 'ATTENDED' | 'ABSENT';
  participantName: string;
  eventId: string;
  disabled?: boolean;
}

// ===================
// COMPONENT
// ===================

export function CheckInButton({
  participantId,
  attendanceStatus,
  participantName,
  eventId,
  disabled = false,
}: CheckInButtonProps) {
  const { checkIn, markAbsent, resetStatus, isUpdating } = useAttendanceMutations(eventId);
  const { toast } = useToast();

  const handleCheckIn = async () => {
    try {
      await checkIn(participantId);
      toast({ title: 'Asistencia registrada', description: `${participantName} marcado como presente` });
    } catch {
      // error toast handled by mutation
    }
  };

  const handleMarkAbsent = async () => {
    try {
      await markAbsent(participantId);
      toast({ title: 'Marcado como ausente', description: `${participantName} marcado como ausente` });
    } catch {
      // error toast handled by mutation
    }
  };

  const handleReset = async () => {
    try {
      await resetStatus(participantId);
      toast({ title: 'Estado reiniciado', description: `Estado de asistencia de ${participantName} reiniciado` });
    } catch {
      // error toast handled by mutation
    }
  };

  if (attendanceStatus === 'ATTENDED') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" disabled={isUpdating || disabled} className="h-8">
            <Badge variant="default" className="bg-green-500 hover:bg-green-600">
              <Check className="mr-1 size-3" />
              Asistió
            </Badge>
            <ChevronDown className="ml-1 size-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleMarkAbsent} disabled={isUpdating}>
            <X className="mr-2 size-4" />
            Marcar como ausente
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleReset} disabled={isUpdating}>
            <RotateCcw className="mr-2 size-4" />
            Reiniciar estado
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (attendanceStatus === 'ABSENT') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" disabled={isUpdating || disabled} className="h-8">
            <Badge variant="destructive">
              <X className="mr-1 size-3" />
              Ausente
            </Badge>
            <ChevronDown className="ml-1 size-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleCheckIn} disabled={isUpdating}>
            <Check className="mr-2 size-4" />
            Marcar como presente
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleReset} disabled={isUpdating}>
            <RotateCcw className="mr-2 size-4" />
            Reiniciar estado
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Pending status
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" disabled={isUpdating || disabled} className="h-8">
          <Badge variant="secondary">Pendiente</Badge>
          <ChevronDown className="ml-1 size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleCheckIn} disabled={isUpdating}>
          <Check className="mr-2 size-4" />
          Marcar como presente
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleMarkAbsent} disabled={isUpdating}>
          <X className="mr-2 size-4" />
          Marcar como ausente
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
